import { getCurrencyPrecision, getPriceMultiplier } from '@cast/utils';

import { Money } from 'components/money';

import { useCostOverTime } from '../../../../../cost-report/hooks/useCostOverTime';

import { useCostOverTimeSection } from '../../../../../cost-report/hooks/useCostOverTimeSection';
import { useCostReportContext } from '../../../../../cost-report/hooks/useCostReportContext';
import {
  AggregatedMetrics,
  HourlyCostPerResource,
  NormalizedCostMode,
} from '../../../../../cost-report/types/costOverTime';
import { useNormalizedCostsControls } from '../_hooks/useNormalizedCostControls';
import { Typography } from '@cast/design-system';

type Props = {
  mapper: (metrics: AggregatedMetrics) => HourlyCostPerResource | undefined;
};

export const ChartMoney = ({ mapper }: Props) => {
  const { activeTab } = useNormalizedCostsControls();
  const { aggregatedMetrics, timeSeriesStep } = useCostOverTime();
  const { costRate } = useCostOverTimeSection();
  const priceMultiplier = getPriceMultiplier(costRate, timeSeriesStep);
  const { isDefaultFilter } = useCostReportContext();
  const hourlyCost = mapper(aggregatedMetrics);

  if (!hourlyCost) {
    return (
      <Typography component="span" variant="inherit">
        $--.--
      </Typography>
    );
  }

  let cost = 0;
  switch (activeTab) {
    case NormalizedCostMode.NORMALIZED: {
      cost = isDefaultFilter
        ? hourlyCost.normalizedSoFar * priceMultiplier
        : hourlyCost.normalizedAverage * priceMultiplier;
      break;
    }
    case NormalizedCostMode.ON_DEMAND: {
      cost = isDefaultFilter
        ? hourlyCost.onDemandSoFar * priceMultiplier
        : hourlyCost.onDemandAverage * priceMultiplier;
      break;
    }
    case NormalizedCostMode.FALLBACK: {
      cost = isDefaultFilter
        ? hourlyCost.fallbackSoFar * priceMultiplier
        : hourlyCost.fallbackAverage * priceMultiplier;
      break;
    }
    case NormalizedCostMode.SPOT: {
      cost = isDefaultFilter
        ? hourlyCost.spotSoFar * priceMultiplier
        : hourlyCost.spotAverage * priceMultiplier;
      break;
    }
  }
  const precision = getCurrencyPrecision(cost);

  return <Money amount={cost} precision={precision} fixed={precision} />;
};
